import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight, History, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import EmptyState from "@/shared/components/EmptyState";
import LoadingOverlay from "@/shared/components/LoadingOverlay";
import PageHeader from "@/shared/components/PageHeader";
import { useIntegrationsQuery } from "../hooks/useIntegrations";

const PAGE_SIZE = 10;

const IntegrationsPage = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, isPlaceholderData, dataUpdatedAt, refetch } = useIntegrationsQuery({
    search, status, page, pageSize: PAGE_SIZE,
  });

  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = data?.data ?? [];
  const hasFilters = search.trim() !== "" || status !== "all";

  useEffect(() => {
    if (data && !isPlaceholderData && page > totalPages) setPage(totalPages);
  }, [data, isPlaceholderData, page, totalPages]);

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Integraciones" subtitle="Cuentas vinculadas a plataformas" />

      <div className="flex flex-wrap items-center gap-3 border-b border-neutral-800 pb-4">
        <Input
          type="search"
          aria-label="Buscar integraciones"
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          placeholder="Cuenta, usuario o plataforma"
          className="w-full max-w-sm border-neutral-700 bg-neutral-800/50 text-neutral-100"
        />
        <Select value={status} onValueChange={(value) => { setStatus(value as typeof status); setPage(1); }}>
          <SelectTrigger aria-label="Credencial" className="w-[180px] border-neutral-700 bg-neutral-800/50 text-neutral-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-neutral-700 bg-neutral-900">
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="active">Con credencial</SelectItem>
            <SelectItem value="inactive">Sin credencial</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 sm:ml-auto">
          <Button asChild variant="outline" size="sm">
            <Link to="/settings"><History className="mr-2 size-4" />Historial de crons</Link>
          </Button>
          <Button
            variant="outline" size="icon" aria-label="Actualizar integraciones" title="Actualizar integraciones"
            onClick={() => void refetch()} disabled={isFetching}
          >
            <RefreshCw className={`size-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-neutral-400">
        <span>{data ? `${total} ${total === 1 ? "integración" : "integraciones"}` : isLoading ? "Cargando integraciones..." : "Listado no disponible"}</span>
        {dataUpdatedAt > 0 && !isPlaceholderData && <span>Última consulta: {format(dataUpdatedAt, "HH:mm:ss")}</span>}
        {isPlaceholderData && <span>Actualizando resultados...</span>}
      </div>

      {isError && (
        <p role="alert" className="text-sm text-red-400">
          {data
            ? "No se pudo actualizar el listado. Se muestra la última consulta disponible."
            : "No se pudieron cargar las integraciones. Volvé a intentar con Actualizar."}
        </p>
      )}

      <div className="min-w-0" aria-busy={isFetching}>
        {rows.length > 0 && (
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow className="border-neutral-800 hover:bg-transparent">
                <TableHead>ID</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Plataforma</TableHead>
                <TableHead>Credencial</TableHead>
                <TableHead>Último refresh correcto</TableHead>
                <TableHead>Fallos consecutivos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((integration) => (
                <TableRow key={integration.id} className="border-neutral-800 hover:bg-neutral-800/50">
                  <TableCell className="font-mono text-xs text-neutral-400">{integration.id}</TableCell>
                  <TableCell className="min-w-[220px] max-w-[320px] break-words font-medium">{integration.name}</TableCell>
                  <TableCell className="min-w-[200px] max-w-[300px] break-words">
                    <div className="flex flex-col gap-1">
                      <span>{integration.userName || integration.userEmail || (integration.userId ? `ID ${integration.userId}` : "Sin usuario vinculado")}</span>
                      {integration.userId && (
                        <span className="text-xs text-neutral-500">{integration.userName && integration.userEmail ? `${integration.userEmail} · ` : ""}ID {integration.userId}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm capitalize">{integration.type}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={integration.status === "active" ? "border-green-800 text-green-400" : "border-neutral-700 text-neutral-400"}>
                      {integration.status === "active" ? "Registrada" : "Ausente"}
                    </Badge>
                  </TableCell>
                  <TableCell className="min-w-[170px] text-neutral-400">
                    {integration.lastSyncAt ? (
                      <time dateTime={integration.lastSyncAt} title={format(new Date(integration.lastSyncAt), "dd/MM/yyyy HH:mm:ss")}>
                        {formatDistanceToNow(new Date(integration.lastSyncAt), { addSuffix: true, locale: es })}
                      </time>
                    ) : "Sin refresh exitoso"}
                  </TableCell>
                  <TableCell className={integration.consecutiveFailures > 0 ? "text-amber-400" : "text-neutral-400"}>
                    {integration.consecutiveFailures}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {isLoading && <LoadingOverlay />}
        {!isLoading && !isError && !isPlaceholderData && page <= totalPages && rows.length === 0 && (
          <EmptyState
            title={hasFilters ? "Sin resultados" : "Sin integraciones"}
            description={hasFilters ? "No hay cuentas que coincidan con estos filtros." : "No hay integraciones registradas actualmente."}
          />
        )}

        {data && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 py-3 text-sm text-neutral-300">
            <span>Página {data.meta.page} de {totalPages}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" aria-label="Página anterior" title="Página anterior"
                onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1 || isFetching}>
                <ChevronLeft className="size-4" />
              </Button>
              <Button variant="outline" size="icon" aria-label="Página siguiente" title="Página siguiente"
                onClick={() => setPage((current) => current + 1)} disabled={page >= totalPages || isFetching}>
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsPage;
